import React, { useState, useEffect } from 'react'
import { useLocation } from 'react-router-dom'
import axios from 'axios'
import { Box, Button, Alert, Typography, Select, MenuItem, FormControl } from '@mui/material'
import DSM_LOGO from '../../../assets/images/DSM-Logo.svg'
import binariiLogo from '../../../assets/images/binariilabs.svg'
import AWSRegisterTextField from '../../atoms/AWSRegistrationPage'
import config from '../../../config'

type AwsPricingCatalogItem = {
  dimension: string
  displayName: string
  description?: string
  annualFee?: number | null
  actionAllowance?: number | null
  currency?: string
  unit?: string
  billingType?: string
  active: boolean
}

type PackageOption = {
  value: string
  label: string
}

const formatPackageLabel = (item: AwsPricingCatalogItem): string => {
  if (item.displayName?.trim()) {
    return item.displayName
  }

  let currencySymbol = ''
  if (item.currency === 'EUR') {
    currencySymbol = '€'
  } else if (item.currency) {
    currencySymbol = `${item.currency} `
  }

  const annualFee =
    typeof item.annualFee === 'number'
      ? `${currencySymbol}${item.annualFee.toLocaleString()}`
      : 'Custom'
  const billingType = item.billingType?.toLowerCase() === 'yearly' ? '/year' : ''
  const actionUnit = item.unit || 'actions'
  const actionAllowance =
    typeof item.actionAllowance === 'number'
      ? ` - ${item.actionAllowance.toLocaleString()} ${actionUnit}`
      : ''

  return `${annualFee}${billingType}${actionAllowance}`.trim()
}

const RegistrationPage: React.FC = () => {
  const location = useLocation()
  const [email, setEmail] = useState('')
  const [username, setUsername] = useState('')
  const [contactNumber, setContactNumber] = useState('')
  const [awsAccountId, setAwsAccountId] = useState('')
  const [customerIdentifier, setCustomerIdentifier] = useState('')
  const [selectedPackage, setSelectedPackage] = useState('')
  const [customAllowance, setCustomAllowance] = useState('')
  const [packageOptions, setPackageOptions] = useState<PackageOption[]>([])
  const [isLoadingPackages, setIsLoadingPackages] = useState(true)
  const [error, setError] = useState('')
  const [success, setSuccess] = useState('')
  const baseURL = config.REACT_APP_BASE_URL

  // Extract customerToken from query parameters
  useEffect(() => {
    const queryParams = new URLSearchParams(location.search)
    const accountAwsId = queryParams.get('accountAwsId')
    const customerMPI = queryParams.get('customerMPI')

    if (!accountAwsId || !customerMPI) {
      setError('Customer token is missing.')
    } else {
      setAwsAccountId(accountAwsId)
      setCustomerIdentifier(customerMPI)
    }
  }, [location.search])

  useEffect(() => {
    const fetchPackageCatalog = async () => {
      try {
        setIsLoadingPackages(true)
        const response = await axios.get<AwsPricingCatalogItem[]>(`${baseURL}aws-packages/catalog`)
        const activePackageOptions = response.data
          .filter(item => item.active)
          .map(item => ({
            value: item.dimension,
            label: formatPackageLabel(item),
          }))

        setPackageOptions(activePackageOptions)
      } catch {
        setError(previousError => previousError || 'Failed to load package catalog.')
      } finally {
        setIsLoadingPackages(false)
      }
    }

    fetchPackageCatalog()
  }, [baseURL])

  useEffect(() => {
    if (!selectedPackage) {
      return
    }

    const hasSelectedPackage = packageOptions.some(option => option.value === selectedPackage)
    if (!hasSelectedPackage) {
      setSelectedPackage('')
      setCustomAllowance('')
    }
  }, [packageOptions, selectedPackage])

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()

    if (!awsAccountId || !customerIdentifier) {
      setError('Customer information is missing.')
      setSuccess('')
      return // Stop further execution
    }

    try {
      // Call the "subscribe user" API
      const response = await axios.post(
        `${baseURL}integration/subscription/aws-marketplace/register`,
        {
          email,
          username,
          contactNumber,
          awsAccountId,
          customerIdentifier,
          packageDimension: selectedPackage,
          customAllowance:
            selectedPackage === 'PACKAGE_CUSTOM' ? parseInt(customAllowance, 10) || null : null,
        }
      )

      if (response.status === 200) {
        setSuccess(response.data) // Show success message from the server
        setError('')
      } else {
        setError(response.data) // Show error message from the server
        setSuccess('')
      }
    } catch (err: unknown) {
      if (err && typeof err === 'object' && 'response' in err) {
        const axiosError = err as { response: { data: string } }
        // Server responded with a status other than 2xx
        setError(axiosError.response.data || 'An error occurred while processing the request.')
      } else {
        // Network or other errors
        setError('Failed to connect to the server. Please try again.')
      }
      setSuccess('')
    }
  }
  return (
    <Box
      sx={{
        height: '100vh', // full viewport height
        flexDirection: 'column', // Stack vertically
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
        background: '#F6F8FC',
        position: 'relative', // IMPORTANT to anchor absolute children
      }}
    >
      <Box sx={{ marginBottom: '46px' }}>
        <img src={DSM_LOGO} alt="dsm" />
      </Box>
      <Box
        sx={{
          maxWidth: '400px',
          width: '100%',
          padding: '40px 24px',
          borderRadius: '12px',
          background: '#FFF',
          boxShadow: '0px 0px 20px 0px rgba(0, 0, 0, 0.06)',
        }}
      >
        {error && <Alert severity="error">{error}</Alert>}
        {success && <Alert severity="success">{success}</Alert>}
        <form onSubmit={handleSubmit}>
          <Box sx={{ marginBottom: '1rem' }}>
            <Typography
              sx={{ color: '#18181B', fontfamily: 'Geist', fontSize: '14px', fontWeight: 500 }}
            >
              Email
            </Typography>
            <AWSRegisterTextField
              placeholder="Email address"
              type="email"
              required
              value={email}
              onChange={e => setEmail(e.target.value)}
            />
          </Box>

          <Box sx={{ marginBottom: '1rem' }}>
            <Typography
              sx={{ color: '#18181B', fontfamily: 'Geist', fontSize: '14px', fontWeight: 500 }}
            >
              Username
            </Typography>
            <AWSRegisterTextField
              placeholder="Username"
              type="text"
              required
              value={username}
              onChange={e => setUsername(e.target.value)}
            />
          </Box>

          <Box sx={{ marginBottom: '1rem' }}>
            <Typography
              sx={{ color: '#18181B', fontfamily: 'Geist', fontSize: '14px', fontWeight: 500 }}
            >
              Contact
            </Typography>
            <AWSRegisterTextField
              placeholder="Contact number"
              type="text"
              required
              value={contactNumber}
              onChange={e => setContactNumber(e.target.value)}
            />
          </Box>

          <Box sx={{ marginBottom: '1rem' }}>
            <Typography
              sx={{
                color: '#18181B',
                fontfamily: 'Geist',
                fontSize: '14px',
                fontWeight: 500,
                marginBottom: '8px',
              }}
            >
              Package Selection
            </Typography>
            <FormControl fullWidth required>
              <Select
                value={selectedPackage}
                onChange={e => setSelectedPackage(e.target.value)}
                displayEmpty
                disabled={isLoadingPackages || packageOptions.length === 0}
                sx={{
                  height: '40px',
                  fontSize: '14px',
                  '& .MuiOutlinedInput-notchedOutline': {
                    borderColor: '#E4E4E7',
                  },
                  '&:hover .MuiOutlinedInput-notchedOutline': {
                    borderColor: '#6E06FF',
                  },
                  '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                    borderColor: '#6E06FF',
                  },
                }}
              >
                <MenuItem value="" disabled>
                  <em>{isLoadingPackages ? 'Loading packages...' : 'Select a package'}</em>
                </MenuItem>
                {packageOptions.map(option => (
                  <MenuItem key={option.value} value={option.value}>
                    {option.label}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Box>

          {selectedPackage === 'PACKAGE_CUSTOM' && (
            <Box sx={{ marginBottom: '1rem' }}>
              <Typography
                sx={{ color: '#18181B', fontfamily: 'Geist', fontSize: '14px', fontWeight: 500 }}
              >
                Custom Allowance (Actions per year)
              </Typography>
              <AWSRegisterTextField
                placeholder="Enter number of actions"
                type="number"
                required
                value={customAllowance}
                onChange={e => setCustomAllowance(e.target.value)}
                inputProps={{ min: '1', step: '1' }}
              />
            </Box>
          )}

          {/* Hidden fields for AWS details */}
          <input type="hidden" name="awsAccountId" value={awsAccountId} />
          <input type="hidden" name="customerIdentifier" value={customerIdentifier} />

          <Button
            type="submit"
            variant="contained"
            fullWidth
            sx={{
              marginTop: '1rem',
              backgroundColor: '#6E06FF',
              color: '#ffffff',
              padding: '8px 16px',
              borderRadius: '8px',
              fontWeight: '500',
              textTransform: 'none',
              justifyContent: 'center',
              '&:hover': {
                backgroundColor: '#5504cc',
              },
            }}
            disabled={isLoadingPackages || packageOptions.length === 0}
          >
            Register
          </Button>
        </form>
      </Box>

      {/* 👇 Footer: Powered by Binarii */}
      <Box
        sx={{
          display: 'flex',
          padding: '1.6rem',
          alignItems: 'center',
          justifyContent: 'center',
          position: 'absolute',
          bottom: 0,
          width: '100%',
          height: '5.4rem',
          backgroundColor: 'transparent',
        }}
      >
        <Typography variant="body2" sx={{ color: '#7A8897', marginRight: '0.5rem' }}>
          Powered by
        </Typography>
        <img src={binariiLogo} style={{ width: '7.2rem', height: '2.5rem' }} alt="binarii logo" />
      </Box>
    </Box>
  )
}

export default RegistrationPage
